/**
 * ModoForjaRadio - Compact premium radio player UI
 * Audio is managed by ModoForjaAudioManager (persistent)
 * Supports both local music and online radio stations
 */

import React from 'react';
import { Play, Pause, Volume2, VolumeX, Radio, Music, ChevronDown } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useModoForja } from '@/contexts/ModoForjaContext';

export default function ModoForjaRadio() {
  const { 
    isRadioPlaying, 
    setRadioPlaying,
    volume,
    isMuted,
    setVolume,
    toggleMute,
    currentTrack,
    tracks,
    tracksLoading,
    sessionType,
    audioSource,
    setAudioSource,
    stations,
    currentStation,
    setCurrentStation,
    stationsLoading,
  } = useModoForja();

  const togglePlay = () => {
    setRadioPlaying(!isRadioPlaying);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
  };

  const formatTrackName = (name: string) => {
    const baseName = name.replace(/\.[^/.]+$/, '');
    return baseName.length > 18 ? baseName.substring(0, 18) + '...' : baseName;
  };

  const isLoading = audioSource === 'local' ? tracksLoading : stationsLoading;
  const hasContent = audioSource === 'local' ? tracks.length > 0 : stations.length > 0;

  // Get current display name
  const getCurrentDisplayName = () => {
    if (audioSource === 'local') {
      return currentTrack ? formatTrackName(currentTrack.file_name) : 'Carregando...';
    } else {
      return currentStation?.name || 'Selecione uma estação';
    }
  };

  // Get source label
  const getSourceLabel = () => {
    if (audioSource === 'local') {
      return 'Forja Radio';
    } else {
      return 'Rádio Online';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-secondary/20 border border-white/[0.04] animate-pulse">
        <div className="w-8 h-8 rounded-full bg-secondary/30" />
        <div className="flex-1">
          <div className="h-2.5 w-14 bg-secondary/30 rounded mb-1.5" />
          <div className="h-3 w-24 bg-secondary/30 rounded" />
        </div>
      </div>
    );
  }

  // No content available
  if (!hasContent && audioSource === 'local' && stations.length === 0) {
    return (
      <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-secondary/10 border border-white/[0.03] opacity-50">
        <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
          <Radio className="w-3.5 h-3.5 text-muted-foreground/50" />
        </div>
        <span className="text-xs text-muted-foreground/60">Sem áudio disponível</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-secondary/20 border border-white/[0.04] backdrop-blur-sm">
      {/* Play Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={togglePlay}
        disabled={!hasContent}
        className={cn(
          "w-8 h-8 rounded-full flex-shrink-0 transition-all",
          isRadioPlaying 
            ? sessionType === 'focus'
              ? "bg-primary/20 text-primary hover:bg-primary/30" 
              : "bg-success/20 text-success hover:bg-success/30"
            : "bg-secondary/30 hover:bg-secondary/50 text-muted-foreground"
        )}
      >
        {isRadioPlaying ? (
          <Pause className="w-3.5 h-3.5" />
        ) : (
          <Play className="w-3.5 h-3.5 ml-0.5" />
        )}
      </Button>

      {/* Source Selector & Track Info */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex-1 min-w-0 text-left hover:bg-secondary/20 rounded-lg px-1.5 py-1 transition-colors">
            <div className="flex items-center gap-1">
              {audioSource === 'local' ? (
                <Music className={cn(
                  "w-2.5 h-2.5 flex-shrink-0",
                  isRadioPlaying 
                    ? sessionType === 'focus' ? "text-primary" : "text-success"
                    : "text-muted-foreground/50"
                )} />
              ) : (
                <Radio className={cn(
                  "w-2.5 h-2.5 flex-shrink-0",
                  isRadioPlaying 
                    ? sessionType === 'focus' ? "text-primary" : "text-success"
                    : "text-muted-foreground/50"
                )} />
              )}
              <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider font-medium">
                {getSourceLabel()}
              </span>
              <ChevronDown className="w-2.5 h-2.5 text-muted-foreground/40" />
            </div>
            <p className="text-[11px] font-medium text-foreground/80 truncate mt-0.5">
              {getCurrentDisplayName()}
            </p>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {/* Source Selection */}
          <DropdownMenuLabel className="text-xs text-muted-foreground">Fonte de Áudio</DropdownMenuLabel>
          <DropdownMenuItem 
            onClick={() => setAudioSource('local')}
            className={cn(audioSource === 'local' && "bg-secondary/50")}
          >
            <Music className="w-4 h-4 mr-2" />
            <span>Músicas Locais</span>
            {tracks.length > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">({tracks.length})</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setAudioSource('radio')}
            className={cn(audioSource === 'radio' && "bg-secondary/50")}
          >
            <Radio className="w-4 h-4 mr-2" />
            <span>Rádios Online</span>
            {stations.length > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">({stations.length})</span>
            )}
          </DropdownMenuItem>

          {/* Stations List (if radio source) */}
          {audioSource === 'radio' && stations.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Estações</DropdownMenuLabel>
              {stations.map((station) => (
                <DropdownMenuItem
                  key={station.id}
                  onClick={() => setCurrentStation(station)}
                  className={cn(
                    "flex items-center gap-2",
                    currentStation?.id === station.id && "bg-primary/10"
                  )}
                >
                  <div className="w-5 h-5 rounded bg-secondary/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {station.favicon_url ? (
                      <img
                        src={station.favicon_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Radio className="w-3 h-3 text-muted-foreground" />
                    )}
                  </div>
                  <span className="truncate text-sm">{station.name}</span>
                  {currentStation?.id === station.id && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Volume - compact */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMute}
          className="w-6 h-6 text-muted-foreground/50 hover:text-foreground"
        >
          {isMuted || volume === 0 ? (
            <VolumeX className="w-3 h-3" />
          ) : (
            <Volume2 className="w-3 h-3" />
          )}
        </Button>
        <Slider
          value={[isMuted ? 0 : volume]}
          max={1}
          step={0.01}
          onValueChange={handleVolumeChange}
          className="w-14"
        />
      </div>
    </div>
  );
}
